/*
 * File Path: client/src/components/dashboard/devices/ManageDevices.js
 * Description: Component to display and manage user's devices, with specific handling for various sensor types.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * Version: 4.5.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-06-22
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../common/LoadingSpinner.js';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal.js';
import EditDeviceModal from './EditDeviceModal.js';
import { fetchGroupedDevices, updateDevice, deleteDevice, fetchEditableProperties } from '../../../api/authApi';
import {
    FaLightbulb, FaThermometerHalf, FaPlug, FaVideo, FaLock, FaTint,
    FaWind, FaQuestionCircle, FaEdit, FaTrashAlt, FaPowerOff, FaToggleOn, FaToggleOff,
    FaDoorOpen, FaDoorClosed, FaFan, FaBroadcastTower, FaThermometerFull,
    FaSmog, FaBolt, FaTemperatureHigh, FaTemperatureLow,
    FaBatteryFull, FaBatteryThreeQuarters, FaBatteryHalf, FaBatteryQuarter, FaBatteryEmpty,
    FaShareAlt
} from 'react-icons/fa';

import './ManageDevices.css';

// Icon Mapping
const deviceIcons = {
    'Light': FaLightbulb, 'RGBLight': FaLightbulb, 'DimmableLight': FaLightbulb,'Bulb': FaLightbulb, 'light_bulb': FaLightbulb, 'light_bulbs': FaLightbulb, 'Wall_Switch': FaLightbulb,
    'Switch': FaPlug, 'Outlet': FaPlug, 'SmartPlug': FaPlug, 'Relay': FaBolt, 'Power_Outlet': FaPlug,
    'TemperatureSensor': FaThermometerHalf, 'HumiditySensor': FaTint, 'MotionSensor': FaVideo,
    'DoorSensor': FaDoorOpen, 'WindowSensor': FaDoorOpen, 'ContactSensor': FaDoorOpen,
    'SmokeSensor': FaSmog, 'GasSensor': FaSmog, 'TempHumSensor': FaThermometerHalf, 'SHT4X_T_H_SENSOR': FaThermometerHalf,
    'Thermostat': FaThermometerFull, 'AirConditioner': FaWind, 'Fan': FaFan, 'Heater': FaTemperatureHigh, 'Cooler': FaTemperatureLow,
    'Camera': FaVideo, 'Lock': FaLock, 'Siren': FaBroadcastTower,
    'WaterLeakSensor': FaTint,
    'Flood_Detector': FaTint,
    'Leak_Detector': FaTint,
    'default': FaQuestionCircle
};
const DefaultIcon = FaQuestionCircle;

const POLLING_INTERVAL_MS = 1000;

function ManageDevices() {
    const { t } = useTranslation();
    const [groupedDevices, setGroupedDevices] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const pollingIntervalRef = useRef(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deviceToDelete, setDeviceToDelete] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);
    const [editableProperties, setEditableProperties] = useState([]);

    const triggerRefresh = useCallback(() => setRefreshTrigger(prev => prev + 1), []);

    const loadAndGroupDevices = useCallback(async (isInitialLoad = false) => {
        if (isInitialLoad) {
            setLoading(true);
            setError(null);
        }
        try {
            const flatDeviceList = await fetchGroupedDevices();
            const grouped = flatDeviceList.reduce((acc, device) => {
                const propId = device.property_id;
                if (!acc[propId]) {
                    acc[propId] = {
                        name: device.property_name,
                        isOwner: device.is_property_owner,
                        permissions: device.shared_permissions || [],
                        devices: []
                    };
                }
                acc[propId].devices.push({
                    device_id: device.device_id,
                    device_name: device.device_name,
                    device_type: device.device_type,
                    device_status: device.device_status || 'offline',
                    device_enabled: device.device_enabled !== undefined ? device.device_enabled : true,
                    device_state: device.device_state,
                    device_value: device.device_value,
                    location: device.location,
                    battery_level: device.battery_level,
                    property_id: device.property_id
                });
                return acc;
            }, {});
            setGroupedDevices(grouped);
            if (!isInitialLoad) setError(null);
        } catch (err) {
            console.error("[ManageDevices] Error loading/grouping devices:", err);
            if (isInitialLoad) setError(err.message || t('errors.api.requestFailed'));
        } finally {
            if (isInitialLoad) setLoading(false);
        }
    }, [t]);
    
    useEffect(() => {
        loadAndGroupDevices(true);
    }, [loadAndGroupDevices, refreshTrigger]);

    useEffect(() => {
        let isMounted = true;
        const fetchProps = async () => {
            try {
                const props = await fetchEditableProperties();
                if(isMounted) setEditableProperties(props || []);
            } catch (err) {
                console.error("Failed to fetch editable properties:", err);
                if(isMounted) setError(prev => prev || err.message || 'Failed to load properties for editing.');
            }
        };
        fetchProps();
        return () => { isMounted = false };
    }, []);

    useEffect(() => {
        const pollData = () => loadAndGroupDevices(false);
        if (!loading) {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(pollData, POLLING_INTERVAL_MS);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [loading, loadAndGroupDevices]);

    const handleToggleEnableDevice = async (deviceToToggle) => {
        const deviceId = deviceToToggle.device_id;
        if (actionLoading === deviceId) return;

        setActionLoading(deviceId); 
        setError(null);
        
        try {
            const newEnabledState = !deviceToToggle.device_enabled;
            await updateDevice(deviceId, { enabled: newEnabledState });
            triggerRefresh(); 
        } catch (err) {
            setError(err.message || `Failed to toggle device.`);
        } finally {
            setActionLoading(null);
        }
    };

    const handleEditDevice = (device) => {
        setEditingDevice(device);
        setShowEditModal(true);
        setError(null);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        setEditingDevice(null);
        triggerRefresh();
    };

    const handleDeleteDevice = (deviceId, deviceName) => {
        setDeviceToDelete({ id: deviceId, name: deviceName });
        setShowDeleteModal(true);
        setError(null);
    };

    const confirmDelete = async () => {
        if (!deviceToDelete) return;
        const { id: deviceId } = deviceToDelete;
        setActionLoading(deviceId);
        setError(null);
        try {
            await deleteDevice(deviceId);
            setShowDeleteModal(false);
            setDeviceToDelete(null);
            triggerRefresh();
        } catch (err) {
            console.error(`[ManageDevices] Error deleting device ${deviceId}:`, err);
            setError(err.message || t('devices.error.deleteFailed', 'Failed to delete device.'));
        } finally {
            setActionLoading(null);
        }
    };
    
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setDeviceToDelete(null);
    };

    const sortedPropertyIds = useMemo(() => {
        return Object.keys(groupedDevices).sort((a, b) => {
            const propA = groupedDevices[a];
            const propB = groupedDevices[b];
            if (propA.isOwner && !propB.isOwner) return -1;
            if (!propA.isOwner && propB.isOwner) return 1;
            return propA.name.localeCompare(b.name);
        });
    }, [groupedDevices]);


    if (loading && Object.keys(groupedDevices).length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="devices-container">
            <div className="devices-title-container">
                <h1>{t('dashboard.devices.title')}</h1>
            </div>

            {error && !showDeleteModal && !showEditModal && <div className="form-error" style={{ textAlign: 'center', marginBottom: '20px' }}>{error}</div>}

            {sortedPropertyIds.length === 0 && !loading && !error && (
                <p style={{ textAlign: 'center', marginTop: '30px' }}>{t('devices.noDevicesFound', 'No devices found for your properties.')}</p>
            )}

            {sortedPropertyIds.map(propertyId => {
                const propertyGroup = groupedDevices[propertyId];
                if (!propertyGroup || !propertyGroup.devices) return null;

                const canManageDevice = propertyGroup.isOwner || (Array.isArray(propertyGroup.permissions) && propertyGroup.permissions.includes('manage'));
                
                return (
                    <div key={propertyId} className="property-device-group">
                        <h2 className="property-group-header">
                            {propertyGroup.name} {t('devices.headerSuffix')}
                            {!propertyGroup.isOwner && (
                                <span className="shared-property-indicator" title={t('properties.sharedWithYou')}>
                                    ({t('properties.shared')})
                                </span>
                            )}
                        </h2>

                        <div className="devices-grid">
                            {propertyGroup.devices.map(device => {
                                if (!device || !device.device_id) return null;
                                
                                const isShtSensor = device.device_type === 'SHT4X_T_H_SENSOR';
                                const isValueDevice = device.device_type === 'Wall_Switch' || device.device_type === 'Power_Outlet' || device.device_type === 'Leak_Detector';

                                const isMultiBulb = device.device_type === 'light_bulbs';
                                let IconComponent = deviceIcons[device.device_type] || DefaultIcon;
                                let bulbStates = { bulb1: false, bulb2: false };
                                
                                let displayValue = null;
                                if (isValueDevice) {
                                    displayValue = device.device_value;
                                } else if (isShtSensor) {
                                    const tempMatch = device.device_value ? device.device_value.match(/Temperature: ([\d.]+)/) : null;
                                    const humMatch = device.device_value ? device.device_value.match(/Humidity: ([\d.]+)/) : null;
                                    const temperature = tempMatch ? tempMatch[1] : null;
                                    const humidity = humMatch ? humMatch[1] : null;

                                    if (temperature && humidity) {
                                        displayValue = `${temperature}°C / ${humidity}%`;
                                    } else if (temperature) {
                                        displayValue = `${temperature}°C`;
                                    } else if (humidity) {
                                        displayValue = `${humidity}%`;
                                    }
                                }

                                if (isMultiBulb) {
                                    const states = (device.device_state || 'false,false').split(',');
                                    bulbStates.bulb1 = states[0]?.trim().toLowerCase() === 'true';
                                    bulbStates.bulb2 = states[1]?.trim().toLowerCase() === 'true';
                                } else if (device.device_type === 'DoorSensor' || device.device_type === 'WindowSensor' || device.device_type === 'ContactSensor') {
                                    IconComponent = (String(device.device_state).toUpperCase() === 'OPEN' || String(device.device_state).toUpperCase() === 'TRUE') ? FaDoorOpen : FaDoorClosed;
                                }

                                const isOn = String(device.device_state).toUpperCase() === 'ON' || String(device.device_state).toUpperCase() === 'TRUE' || Number(device.value) > 0;
                                
                                const cardClasses = ['device-card', !device.device_enabled && 'disabled', device.device_status !== 'online' && 'offline', !propertyGroup.isOwner && 'shared-device'].filter(Boolean).join(' ');
                                
                                let batteryClass = '';
                                let BatteryIcon = null;
                                if (device.battery_level !== null && device.battery_level !== undefined) {
                                    const level = Number(device.battery_level);
                                    if (level > 85) { BatteryIcon = FaBatteryFull; batteryClass = 'battery-high';}
                                    else if (level > 60) { BatteryIcon = FaBatteryThreeQuarters; batteryClass = 'battery-high'; }
                                    else if (level > 35) { BatteryIcon = FaBatteryHalf; batteryClass = 'battery-medium'; }
                                    else if (level > 10) { BatteryIcon = FaBatteryQuarter; batteryClass = 'battery-low'; }
                                    else { BatteryIcon = FaBatteryEmpty; batteryClass = 'battery-low'; }
                                }

                                const iconClassName = [
                                    'device-icon',
                                    isOn && !isShtSensor ? 'on' : 'off', // SHT sensor icon color is handled separately
                                    isShtSensor ? 'sht-sensor-icon' : ''
                                ].filter(Boolean).join(' ');


                                return (
                                    <div key={device.device_id} className={cardClasses} title={!device.device_enabled ? t('devices.status.disabled') : (device.device_status !== 'online' ? t('devices.status.offline') : '')}>
                                        {canManageDevice && (
                                            <div className="device-card-actions">
                                                <button onClick={() => handleEditDevice(device)} title={t('common.edit')} disabled={actionLoading === device.device_id}> <FaEdit /> </button>
                                                <button onClick={() => handleToggleEnableDevice(device)} title={device.device_enabled ? t('devices.actions.disable') : t('devices.actions.enable')} disabled={actionLoading === device.device_id} className={`enable-disable-button ${device.device_enabled ? 'enabled' : 'disabled'}`}>
                                                    {actionLoading === device.device_id ? <LoadingSpinner size="extrasmall" /> : (device.device_enabled ? <FaToggleOff /> : <FaToggleOn />)}
                                                </button>
                                                <button onClick={() => handleDeleteDevice(device.device_id, device.device_name)} title={t('common.delete')} disabled={actionLoading === device.device_id}> <FaTrashAlt /> </button>
                                            </div>
                                        )}
                                        {device.device_status !== 'online' && device.device_enabled && <div className="offline-indicator" title={t('devices.status.offline')}> <FaPowerOff /> </div>}
                                        {!propertyGroup.isOwner && <div className="device-shared-indicator" title={t('properties.sharedWithYou')}> <FaShareAlt /> </div>}
                                        
                                        {(isValueDevice || isShtSensor) && displayValue && (<p className="device-value-top">{displayValue}</p>)}

                                        {isMultiBulb ? (
                                            <div className="multi-bulb-container">
                                                <div className="bulb-control">
                                                    <div>
                                                        <FaLightbulb className={`device-icon ${bulbStates.bulb1 ? 'on' : 'off'}`} />
                                                    </div>
                                                </div>
                                                <div className="bulb-control">
                                                    <div>
                                                        <FaLightbulb className={`device-icon ${bulbStates.bulb2 ? 'on' : 'off'}`} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bulb-container" >
                                                <IconComponent className={iconClassName} />
                                            </div>
                                        )}
                                        
                                        {device.location && <p className="device-location">{device.location}</p>}
                                        <p className="device-name">{device.device_name}</p>
                                        
                                        <div className="device-state">
                                            {!isValueDevice && !isShtSensor && device.device_value && <span className="device-value">{device.device_value}</span>}
                                            {BatteryIcon && ( <span className={`device-battery ${batteryClass}`}> <BatteryIcon className="battery-icon"/> {device.battery_level}% </span> )}
                                            {!device.device_enabled && <span className="device-disabled-text">{t('devices.status.disabled')}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            <DeleteConfirmationModal isOpen={showDeleteModal} onClose={cancelDelete} onConfirm={confirmDelete} itemName={deviceToDelete?.name || ''} isLoading={actionLoading === deviceToDelete?.id} />
            <EditDeviceModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} onUpdateSuccess={handleEditSuccess} device={editingDevice} availableProperties={editableProperties} />

            <div className="dashboard-footer-copyright">
                &copy; {new Date().getFullYear()} beinSmart.cloud. {t('footer.allRightsReserved')}
            </div>
        </div>
    );
}

export default ManageDevices;
